import {
  CardElevation,
  MarketingItem,
  ProductCardContainer,
  ProductCardDivider,
  DeprecatedProductsCardsList,
  ShadowBehavior,
} from '@wix/package-picker-components';
import React, { ReactNode } from 'react';
import { ProductCardData } from 'types';
import { Box } from '@wix/design-system';
import { observer } from 'mobx-react';

interface CardsContainerProps {
  productsCardData: ProductCardData[];
  shouldDisableFeature?: (feature: MarketingItem) => boolean;
}

function CardsContainerComp({
  productsCardData,
  shouldDisableFeature,
}: CardsContainerProps) {
  const maxFeaturesAmount: number = productsCardData
    .map((productCard) => productCard.data.features.length)
    .sort()
    .reverse()[0];

  function getShadowBehavior(currentProductIndex: number): ShadowBehavior {
    return productsCardData[currentProductIndex].data.isRecommended ||
      productsCardData[currentProductIndex + 1]?.data.isRecommended
      ? ShadowBehavior.Shadow
      : ShadowBehavior.Border;
  }

  return (
    <Box backgroundColor='white' width='100%'>
      <DeprecatedProductsCardsList marketingItemsAmount={maxFeaturesAmount}>
        {productsCardData.map((product, index) => {
          const { data, components } = product;
          const { features, isCurrentPlan, isRecommended, id } = data;
          const { cta, hat, price, title } = components;
          const childComponents: ReactNode[] = [];
          const paddedFeaturesAmount = maxFeaturesAmount - features.length;
          const paddedFeatures = Array(paddedFeaturesAmount).fill({ text: '' });
          const cardFeatures: MarketingItem[] = [
            ...features.map((feature) => ({
              ...feature,
              disabled: shouldDisableFeature?.(feature),
            })),
            ...paddedFeatures,
          ];
          const shadowBehavior = getShadowBehavior(index);
          const elevated =
            isRecommended || isCurrentPlan
              ? CardElevation.Medium
              : CardElevation.None;

          childComponents.push(
            <ProductCardContainer
              key={`${id}-cardContainer`}
              highlighted={isRecommended}
              elevated={elevated}
              hat={hat}
              header={title}
              pricing={price}
              cta={cta}
              marketingItems={cardFeatures}
            />,
          );

          if (index < productsCardData.length - 1) {
            childComponents.push(
              <ProductCardDivider
                key={`${id}-divider`}
                shadowBehavior={shadowBehavior}
              />,
            );
          }

          return childComponents;
        })}
      </DeprecatedProductsCardsList>
    </Box>
  );
}

export const CardsContainer = observer(CardsContainerComp);
